import React, { useEffect, useRef } from 'react';
import AudioAnalyzer from '../../utils/audio-context';

const lerp = (v0: number, v1: number, t: number): number => v0 * (1 - t) + v1 * t;

interface AudioMeterProps {
	inputStream?: MediaStream;
	color: string;
}

export default function AudioMeter(props: AudioMeterProps) {
	const { inputStream, color } = props;
	
	const scale = 5;

	const canvas = useRef<HTMLCanvasElement>();
	const currentVol = useRef(0);
	const analyzer = useRef<AudioAnalyzer | undefined>(undefined);
	const targetVol = useRef(0);
	const interval = useRef<number | undefined>(undefined);
	

	const setVol = (vol: number) => {
		targetVol.current = vol * scale;
	};
	
	const componentWillUnmount = () => {
		if (analyzer) {
			if (interval.current) window.cancelAnimationFrame(interval.current);
			analyzer.current?.destroy();
		}
	}

	const componentDidMount = () => {
		if (inputStream && inputStream.getAudioTracks()[0]) {
			if (analyzer) {
				analyzer.current?.destroy();
			}

			const analyzerNew = new AudioAnalyzer(inputStream, setVol, 50);
			analyzer.current = analyzerNew;
			draw();
		}
	}

	useEffect(() => {
		componentDidMount();
		return () => componentWillUnmount();
	}, []);

	const draw = () => {
		interval.current = window.requestAnimationFrame(draw);

		const ctx = canvas?.current?.getContext('2d')

		ctx?.clearRect(0, 0, 160, 120);

		const segments = 160 / 6;
		const yOne = segments * 2;
		const yTwo = segments * 3;
		const yThree = segments * 4;
		const midPoint = 120 / 2;
		currentVol.current = Math.min(lerp(targetVol.current, currentVol.current, 0.8), 50);

		if (ctx) {
			ctx.beginPath();
			ctx.moveTo(yOne, midPoint - currentVol.current / 2);
			ctx.lineTo(yOne, midPoint + currentVol.current / 2);
			ctx.strokeStyle = color;
			ctx.lineCap = 'round';
			ctx.lineWidth = 20;
			ctx.stroke();

			ctx.beginPath();
			ctx.moveTo(yTwo, midPoint - currentVol.current);
			ctx.lineTo(yTwo, midPoint + currentVol.current);
			ctx.strokeStyle = color;
			ctx.lineCap = 'round';
			ctx.lineWidth = 20;
			ctx.stroke();

			ctx.beginPath();
			ctx.moveTo(yThree, midPoint - currentVol.current / 2);
			ctx.lineTo(yThree, midPoint + currentVol.current / 2);
			ctx.strokeStyle = color;
			ctx.lineCap = 'round';
			ctx.lineWidth = 20;
			ctx.stroke();
		}
	};

	const streamId = inputStream?.getAudioTracks()[0]?.id;

	useEffect(() => {
		if (inputStream?.getAudioTracks()[0]) {
			if (analyzer.current) {
				analyzer.current.destroy();
				analyzer.current = undefined;
			}

			analyzer.current = new AudioAnalyzer(inputStream, setVol, 50);
			draw();
		}
	}, [streamId]);

	return (
		<canvas 
			width={120}
			height={120}
			className="audio-meter"
			ref={(ref) => { 
				if (ref)canvas.current = ref;
			}}>
		</canvas>
	);
}
